import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/services/logger';
import { FeedbackService, SessionFeedback as ISessionFeedback } from '@/services/feedbackService';
import { CohortService } from '@/services/cohortService';
import { Timestamp } from 'firebase/firestore';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '@/api/firebase/firebase';
import {
  FiPlus,
  FiX,
  FiMessageSquare,
  FiChevronDown,
  FiChevronUp,
  FiDownload
} from 'react-icons/fi';
import { Cohort, CohortSession } from '@/types/cohort';
import { AssessmentTemplate, AssessmentQuestion } from '@/types/assessment';
import { pickDefaultTemplate } from '@/hooks/assessment/useAssessmentTemplates';

type SessionFeedback = ISessionFeedback;

interface TrainerFeedbackProps {
  isAdmin?: boolean;
}

const TrainerFeedback: React.FC<TrainerFeedbackProps> = ({ isAdmin = false }) => {
  const { cohortId } = useParams<{ cohortId: string }>();
  const { currentUser } = useAuth();
  const [feedbackList, setFeedbackList] = useState<SessionFeedback[]>([]);
  const [cohortName, setCohortName] = useState<string>('');
  const [cohortSessions, setCohortSessions] = useState<CohortSession[]>([]);
  const [trainerName, setTrainerName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  // When the form is opened from a specific session row, the session is known
  // and locked (no manual "Today was" picking needed).
  const [sessionLocked, setSessionLocked] = useState(false);
  // The feedback template linked to this cohort (via Assign Trainer). When
  // present, the form is rendered dynamically from its questions.
  const [feedbackTemplate, setFeedbackTemplate] =
    useState<AssessmentTemplate | null>(null);
  // Answers to the dynamic template questions, keyed by question id.
  const [answers, setAnswers] = useState<Record<string, string>>({});

  // Form state
  const [whoTrained, setWhoTrained] = useState('');
  const [date, setDate] = useState('');
  const [todayWas, setTodayWas] = useState('');
  const [attendanceCount, setAttendanceCount] = useState('');
  const [delegatesOnTime, setDelegatesOnTime] = useState<'yes' | 'no' | ''>('');
  const [lateStartTime, setLateStartTime] = useState('');
  const [camerasOffCount, setCamerasOffCount] = useState('');
  const [camerasOffReason, setCamerasOffReason] = useState('');
  const [homeworkSubmittedCount, setHomeworkSubmittedCount] = useState('');
  const [notFollowingUp, setNotFollowingUp] = useState('');
  const [specificIssues, setSpecificIssues] = useState('');
  const [coverageDetails, setCoverageDetails] = useState('');
  const [sessionFeeling, setSessionFeeling] = useState('');
  const [participationRating, setParticipationRating] = useState('');
  const [briefComments, setBriefComments] = useState('');

  useEffect(() => {
    loadCohortAndFeedback();
  }, [cohortId]);

  const loadCohortAndFeedback = async () => {
    try {
      setLoading(true);
      if (!cohortId) {
        setFeedbackList([]);
        return;
      }

      // Load cohort data
      const cohort = await CohortService.getCohortById(cohortId);
      if (cohort) {
        setCohortName(cohort.name);
        setCohortSessions(cohort.sessions || []);

        // Load trainer name from users collection
        if (cohort.trainerAssignment?.trainerId) {
          try {
            const trainerDoc = await getDoc(doc(db, 'users', cohort.trainerAssignment.trainerId));
            if (trainerDoc.exists()) {
              const userData = trainerDoc.data();
              setTrainerName(userData.name || userData.displayName || userData.email || '');
            }
          } catch {
            // Trainer lookup failed, will use fallback from feedback entries
          }
        }

        // Load the feedback template linked to this cohort. When present, the
        // "Add Feedback" form is rendered dynamically from its questions.
        // When the cohort hasn't picked one, fall back to the template marked
        // as default on the Assessment Templates page — or, if none is marked,
        // the first template. Only when no template exists at all do we render
        // the legacy fixed form.
        const loadDefaultFeedbackTemplate = async () => {
          try {
            const snap = await getDocs(
              query(
                collection(db, 'assessmentTemplates'),
                where('type', '==', 'trainer_feedback')
              )
            );
            const candidates = snap.docs.map((d) => ({
              id: d.id,
              ...d.data(),
              createdAt: d.data().createdAt?.toDate?.() || null,
            })) as AssessmentTemplate[];
            const fallback = pickDefaultTemplate(candidates);
            setFeedbackTemplate(fallback || null);
          } catch {
            setFeedbackTemplate(null);
          }
        };

        const feedbackTemplateId =
          cohort.trainerAssignment?.feedbackTemplateId;
        if (feedbackTemplateId) {
          try {
            const tplDoc = await getDoc(
              doc(db, 'assessmentTemplates', feedbackTemplateId)
            );
            if (tplDoc.exists()) {
              setFeedbackTemplate({
                id: tplDoc.id,
                ...tplDoc.data(),
              } as AssessmentTemplate);
            } else {
              await loadDefaultFeedbackTemplate();
            }
          } catch {
            // Template lookup failed — fall back to the legacy fixed form.
          }
        } else {
          await loadDefaultFeedbackTemplate();
        }
      }

      // Load feedback based on role
      let feedback: SessionFeedback[];
      if (isAdmin) {
        // Admins see all feedback for the cohort
        feedback = await FeedbackService.getFeedbackByCohort(cohortId);
      } else {
        // Trainers see only their own feedback
        if (currentUser?.id) {
          feedback = await FeedbackService.getFeedbackByTrainer(currentUser.id);
          // Filter by current cohort
          feedback = feedback.filter(f => f.cohortId === cohortId);
        } else {
          feedback = [];
        }
      }

      setFeedbackList(feedback);

      // Fallback: get trainer name from feedback entries if user lookup didn't work
      if (!trainerName && feedback.length > 0) {
        const firstWithName = feedback.find(f => f.trainerName);
        if (firstWithName) {
          setTrainerName(firstWithName.trainerName);
        }
      }
    } catch (error) {
      logger.error('Failed to load trainer feedback', error, 'TRAINER_FEEDBACK');
      setFeedbackList([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setWhoTrained('');
    setDate('');
    setTodayWas('');
    setAttendanceCount('');
    setDelegatesOnTime('');
    setLateStartTime('');
    setCamerasOffCount('');
    setCamerasOffReason('');
    setHomeworkSubmittedCount('');
    setNotFollowingUp('');
    setSpecificIssues('');
    setCoverageDetails('');
    setSessionFeeling('');
    setParticipationRating('');
    setBriefComments('');
    setAnswers({});
  };

  // Local YYYY-MM-DD for the date input default.
  const todayISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate()
    ).padStart(2, '0')}`;
  };

  // Open the feedback form. When opened from a session row, that session and
  // the cohort are known and recorded automatically; the date defaults to today.
  const openFeedbackForm = (sessionName?: string) => {
    resetForm();
    setDate(todayISO());
    setWhoTrained(cohortName);
    setTodayWas(sessionName || '');
    setSessionLocked(!!sessionName);
    setShowForm(true);
  };

  const handleSubmitFeedback = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!cohortId || !currentUser?.id) {
      logger.error('Missing cohortId or currentUser', null, 'TRAINER_FEEDBACK');
      return;
    }

    try {
      setSubmitting(true);

      // Shared context — cohort, session and trainer are known automatically.
      const baseData = {
        cohortId,
        trainerId: currentUser.id,
        trainerName: currentUser.name || '',
        whoTrained,
        date,
        todayWas,
      };

      const feedbackData = feedbackTemplate
        ? {
            // Dynamic, template-driven submission.
            ...baseData,
            templateId: feedbackTemplate.id,
            templateName: feedbackTemplate.name,
            answers: (feedbackTemplate.questions || []).map((q) => ({
              questionId: q.id,
              question: q.text,
              type: q.type,
              answer: answers[q.id] || '',
            })),
          }
        : {
            // Legacy fixed-form submission.
            ...baseData,
            attendanceCount,
            delegatesOnTime,
            lateStartTime,
            camerasOffCount,
            camerasOffReason,
            homeworkSubmittedCount,
            notFollowingUp,
            specificIssues,
            coverageDetails,
            sessionFeeling,
            participationRating,
            briefComments,
          };

      // Save to Firestore
      await FeedbackService.submitFeedback(feedbackData);

      // Reload feedback list
      await loadCohortAndFeedback();

      // Reset form and hide it
      resetForm();
      setShowForm(false);

      logger.info('Trainer feedback submitted successfully', { cohortId }, 'TRAINER_FEEDBACK');
    } catch (error) {
      logger.error('Failed to submit trainer feedback', error, 'TRAINER_FEEDBACK');
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const setAnswer = (questionId: string, value: string) =>
    setAnswers((prev) => ({ ...prev, [questionId]: value }));

  // Inclusive numeric scale for a rating question (defaults to 1–10).
  const ratingScale = (q: AssessmentQuestion): number[] => {
    const min = Number(q.options?.[0]?.value ?? 1);
    const max = Number(q.options?.[1]?.value ?? 10);
    const lo = Math.min(min, max);
    const hi = Math.max(min, max);
    return Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
  };

  // Render one template question as a form field bound to `answers`.
  const renderDynamicQuestion = (q: AssessmentQuestion, index: number) => {
    const value = answers[q.id] || '';
    return (
      <Card key={q.id} className="p-6">
        <label className="block text-base font-medium text-gray-900 mb-3">
          {index + 1}. {q.text}
          {q.isRequired && <span className="text-red-600"> *</span>}
        </label>

        {q.type === 'text' && (
          <textarea
            value={value}
            onChange={(e) => setAnswer(q.id, e.target.value)}
            placeholder="Your answer"
            rows={3}
            required={q.isRequired}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          />
        )}

        {q.type === 'multiple_choice' && (
          <div className="space-y-2">
            {q.options?.map((opt) => (
              <label
                key={opt.id}
                className="flex items-center space-x-3 cursor-pointer"
              >
                <input
                  type="radio"
                  name={q.id}
                  value={opt.text}
                  checked={value === opt.text}
                  onChange={() => setAnswer(q.id, opt.text)}
                  required={q.isRequired}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="text-gray-700">{opt.text}</span>
              </label>
            ))}
          </div>
        )}

        {q.type === 'rating' && (
          <div className="flex flex-wrap gap-4">
            {ratingScale(q).map((n) => (
              <label
                key={n}
                className="flex items-center space-x-2 cursor-pointer"
              >
                <input
                  type="radio"
                  name={q.id}
                  value={String(n)}
                  checked={value === String(n)}
                  onChange={() => setAnswer(q.id, String(n))}
                  required={q.isRequired}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="text-sm text-gray-700">{n}</span>
              </label>
            ))}
          </div>
        )}
      </Card>
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  // Helper to find feedback for a session - tries multiple matching strategies
  const getFeedbackForSession = (sessionName: string, sessionIndex: number): SessionFeedback | undefined => {
    // 1. Exact match on todayWas
    const exactMatch = feedbackList.find(f => f.todayWas === sessionName);
    if (exactMatch) return exactMatch;

    // 2. Match "Session N" pattern against session index (1-based)
    const sessionNum = sessionIndex + 1;
    const byNumber = feedbackList.find(f =>
      f.todayWas === `Session ${sessionNum}` ||
      f.todayWas === `session ${sessionNum}` ||
      f.todayWas === `${sessionNum}`
    );
    if (byNumber) return byNumber;

    // 3. Partial match (session name contains the todayWas or vice versa)
    const partial = feedbackList.find(f =>
      f.todayWas &&
      (sessionName.toLowerCase().includes(f.todayWas.toLowerCase()) ||
       f.todayWas.toLowerCase().includes(sessionName.toLowerCase()))
    );
    return partial;
  };

  const formatFeedbackDate = (feedback: SessionFeedback): string => {
    // Try createdAt timestamp
    if (feedback.createdAt && typeof feedback.createdAt === 'object' && 'toDate' in feedback.createdAt) {
      return feedback.createdAt.toDate().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    // Fallback to the date field from the form
    if (feedback.date) {
      try {
        const d = new Date(feedback.date);
        if (!isNaN(d.getTime())) {
          return d.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          });
        }
      } catch {
        return feedback.date;
      }
      return feedback.date;
    }
    return '—';
  };

  // Resolve the best trainer name available
  const resolvedTrainerName = trainerName
    || (feedbackList.length > 0 ? feedbackList.find(f => f.trainerName)?.trainerName : '')
    || '';

  // Build session rows:
  // 1. Start with all feedback entries (these always show with data)
  // 2. Then add cohort sessions that don't have feedback yet
  const sessionRows: { name: string; feedback: SessionFeedback | undefined; sessionDate?: string }[] = [];
  const usedSessionNames = new Set<string>();

  // First: add all feedback entries as rows (guaranteed to have data)
  feedbackList.forEach((f, i) => {
    const name = f.todayWas || `Session ${i + 1}`;
    usedSessionNames.add(name.toLowerCase());
    sessionRows.push({ name, feedback: f });
  });

  // Second: add cohort sessions that don't have feedback yet (shown as "Awaiting")
  if (cohortSessions.length > 0) {
    cohortSessions.forEach((session, index) => {
      const name = session.name || `Session ${session.id}`;
      // Check if we already have a feedback row for this session
      const alreadyHasRow = usedSessionNames.has(name.toLowerCase())
        || usedSessionNames.has(`session ${index + 1}`);
      if (!alreadyHasRow) {
        sessionRows.push({ name, feedback: undefined, sessionDate: session.date });
      }
    });
  }

  // Sort: sessions with feedback first (by session name), then awaiting ones
  sessionRows.sort((a, b) => {
    // Extract session number for natural sorting
    const numA = parseInt(a.name.replace(/\D/g, '')) || 999;
    const numB = parseInt(b.name.replace(/\D/g, '')) || 999;
    return numA - numB;
  });

  const LEGACY_FIELDS: { key: keyof SessionFeedback; label: string }[] = [
    { key: 'attendanceCount', label: 'Attendance' },
    { key: 'delegatesOnTime', label: 'Delegates on time' },
    { key: 'lateStartTime', label: 'Late start time' },
    { key: 'camerasOffCount', label: 'Cameras off' },
    { key: 'camerasOffReason', label: 'Cameras off reason' },
    { key: 'homeworkSubmittedCount', label: 'Homework submitted' },
    { key: 'notFollowingUp', label: 'Not following up' },
    { key: 'specificIssues', label: 'Specific issues' },
    { key: 'coverageDetails', label: 'Coverage details' },
    { key: 'sessionFeeling', label: 'Session feeling' },
    { key: 'participationRating', label: 'Participation rating' },
    { key: 'briefComments', label: 'Comments' },
  ];

  // One row per session; one column per question asked across all feedback.
  const handleExportCsv = () => {
    const questions: string[] = [];
    feedbackList.forEach((f) =>
      (f.answers || []).forEach((a) => {
        if (a.question && !questions.includes(a.question)) questions.push(a.question);
      })
    );
    const legacyFields = LEGACY_FIELDS.filter(({ key }) =>
      feedbackList.some((f) => !f.answers?.length && f[key])
    );

    const header = [
      'Session',
      'Status',
      'Date',
      'Trainer',
      ...questions,
      ...legacyFields.map((f) => f.label),
    ];
    const rows = sessionRows.map(({ name, feedback, sessionDate }) => {
      if (!feedback) {
        const row = [name, 'Awaiting feedback', sessionDate || '', resolvedTrainerName];
        return [...row, ...Array(header.length - row.length).fill('')];
      }
      return [
        name,
        'Submitted',
        feedback.date || formatFeedbackDate(feedback),
        feedback.trainerName || resolvedTrainerName,
        ...questions.map(
          (q) => feedback.answers?.find((a) => a.question === q)?.answer || ''
        ),
        ...legacyFields.map(({ key }) => String(feedback[key] ?? '')),
      ];
    });

    const escape = (value: string) =>
      /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
    const csv = [header, ...rows]
      .map((row) => row.map((v) => escape(String(v ?? ''))).join(','))
      .join('\r\n');

    // BOM so Excel reads the file as UTF-8.
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeName = (cohortName || 'cohort').replace(/[^a-zA-Z0-9-_]+/g, '_');
    link.href = url;
    link.download = `Trainer_Feedback_${safeName}_${todayISO()}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Trainer Feedback</h1>
          <p className="text-gray-500 mt-1 text-[15px]">
            Here's an overview of the feedback provided by trainers for this cohort's sessions.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={sessionRows.length === 0}
            className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors bg-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiDownload className="w-4 h-4" />
            Export Report
          </button>
          {!showForm && (
            <Button
              onClick={() => openFeedbackForm()}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium"
            >
              <FiPlus size={18} />
              Add Feedback
            </Button>
          )}
        </div>
      </div>

      {/* Feedback Form - Toggleable */}
      {showForm && (
        <Card className="p-6 border-2 border-blue-500">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900">New Session Feedback</h2>
            <button
              onClick={() => setShowForm(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              type="button"
            >
              <FiX size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmitFeedback} className="space-y-6">
          {/* Who did you train — auto-filled from the cohort */}
          <Card className="p-6">
            <label className="block text-base font-medium text-gray-900 mb-3">
              Who did you train? <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              value={whoTrained}
              readOnly
              className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700 cursor-not-allowed"
              required
            />
            <p className="mt-2 text-xs text-gray-500">
              Automatically recorded from this cohort.
            </p>
          </Card>

          {/* Today's Date — pre-filled with today */}
          <Card className="p-6">
            <label className="block text-base font-medium text-gray-900 mb-3">
              Today's Date <span className="text-red-600">*</span>
            </label>
            <div className="text-sm text-gray-600 mb-2">Date</div>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </Card>

          {/* Today was — known and locked when added from a session row */}
          <Card className="p-6">
            <label className="block text-base font-medium text-gray-900 mb-3">
              Today was
            </label>
            {sessionLocked ? (
              <>
                <input
                  type="text"
                  value={todayWas}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700 cursor-not-allowed"
                />
                <p className="mt-2 text-xs text-gray-500">
                  Automatically recorded for this session.
                </p>
              </>
            ) : (
              <select
                value={todayWas}
                onChange={(e) => setTodayWas(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="">Choose</option>
                {(cohortSessions.length > 0
                  ? cohortSessions.map(
                      (s, i) => s.name || `Session ${i + 1}`
                    )
                  : Array.from({ length: 6 }, (_, i) => `Session ${i + 1}`)
                ).map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            )}
          </Card>

          {feedbackTemplate ? (
            <>
              {(feedbackTemplate.questions || []).map((q, i) =>
                renderDynamicQuestion(q, i)
              )}
              {(feedbackTemplate.questions || []).length === 0 && (
                <Card className="p-6">
                  <p className="text-sm text-gray-500">
                    This feedback template has no questions yet. Add questions to
                    it on the Assessment Templates page.
                  </p>
                </Card>
              )}
            </>
          ) : (
            <>
          {/* How many people attended */}
          <Card className="p-6">
            <label className="block text-base font-medium text-gray-900 mb-3">
              How many people attended the training today? <span className="text-red-600">*</span>
            </label>
            <select
              value={attendanceCount}
              onChange={(e) => setAttendanceCount(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              required
            >
              <option value="">Choose</option>
              {Array.from({ length: 18 }, (_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1}</option>
              ))}
            </select>
          </Card>

          {/* Did delegates arrive on time */}
          <Card className="p-6">
            <label className="block text-base font-medium text-gray-900 mb-3">
              Did delegates arrive on time? Did the workshop start on time? <span className="text-red-600">*</span>
            </label>
            <div className="space-y-2">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  value="yes"
                  checked={delegatesOnTime === 'yes'}
                  onChange={(e) => setDelegatesOnTime(e.target.value as 'yes')}
                  className="w-4 h-4 text-blue-600"
                  required
                />
                <span className="text-gray-700">Yes</span>
              </label>
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  value="no"
                  checked={delegatesOnTime === 'no'}
                  onChange={(e) => setDelegatesOnTime(e.target.value as 'no')}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="text-gray-700">No</span>
              </label>
            </div>
          </Card>

          {/* If no, how late */}
          <Card className="p-6">
            <label className="block text-base font-medium text-gray-900 mb-3">
              If no, how late did it start?
            </label>
            <input
              type="text"
              value={lateStartTime}
              onChange={(e) => setLateStartTime(e.target.value)}
              placeholder="Your answer"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </Card>

          {/* Cameras off count */}
          <Card className="p-6">
            <label className="block text-base font-medium text-gray-900 mb-3">
              How many delegates did NOT have their cameras on the whole time? <span className="text-red-600">*</span>
            </label>
            <select
              value={camerasOffCount}
              onChange={(e) => setCamerasOffCount(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              required
            >
              <option value="">Choose</option>
              {Array.from({ length: 18 }, (_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1}</option>
              ))}
            </select>
          </Card>

          {/* Reason for cameras off */}
          <Card className="p-6">
            <label className="block text-base font-medium text-gray-900 mb-3">
              Reason given for cameras off?
            </label>
            <input
              type="text"
              value={camerasOffReason}
              onChange={(e) => setCamerasOffReason(e.target.value)}
              placeholder="Your answer"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </Card>

          {/* Homework submitted */}
          <Card className="p-6">
            <label className="block text-base font-medium text-gray-900 mb-3">
              How many delegates submitted the homework assignment and received email feedback from you (question relevant for Session 2 and Session 3)? <span className="text-red-600">*</span>
            </label>
            <select
              value={homeworkSubmittedCount}
              onChange={(e) => setHomeworkSubmittedCount(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              required
            >
              <option value="">Choose</option>
              {Array.from({ length: 18 }, (_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1}</option>
              ))}
            </select>
          </Card>

          {/* Delegates not following up */}
          <Card className="p-6">
            <label className="block text-base font-medium text-gray-900 mb-3">
              Are there delegates who are not following/keeping up with group. If so, who?
            </label>
            <textarea
              value={notFollowingUp}
              onChange={(e) => setNotFollowingUp(e.target.value)}
              placeholder="Your answer"
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </Card>

          {/* Specific issues */}
          <Card className="p-6">
            <label className="block text-base font-medium text-gray-900 mb-3">
              Any specific issues you encountered today?
            </label>
            <textarea
              value={specificIssues}
              onChange={(e) => setSpecificIssues(e.target.value)}
              placeholder="Your answer"
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </Card>

          {/* Coverage details */}
          <Card className="p-6">
            <label className="block text-base font-medium text-gray-900 mb-3">
              Where did you get up to today? What was the last thing you covered?
            </label>
            <textarea
              value={coverageDetails}
              onChange={(e) => setCoverageDetails(e.target.value)}
              placeholder="Your answer"
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </Card>

          {/* Rating matrix */}
          <Card className="p-6">
            <h3 className="text-base font-medium text-gray-900 mb-4">
              Your overall feeling about today
            </h3>

            {/* Session feeling */}
            <div className="mb-6">
              <label className="block text-sm text-gray-700 mb-3">
                How do you feel today's session went?
              </label>
              <div className="flex flex-wrap gap-4">
                {['Poor', 'Fair', 'Satisfactory', 'Very good', 'Excellent'].map((option) => (
                  <label key={option} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      value={option}
                      checked={sessionFeeling === option}
                      onChange={(e) => setSessionFeeling(e.target.value)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Participation rating */}
            <div>
              <label className="block text-sm text-gray-700 mb-3">
                How was the participation and engagement?
              </label>
              <div className="flex flex-wrap gap-4">
                {['Poor', 'Fair', 'Satisfactory', 'Very good', 'Excellent'].map((option) => (
                  <label key={option} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      value={option}
                      checked={participationRating === option}
                      onChange={(e) => setParticipationRating(e.target.value)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
            </div>
          </Card>

          {/* Brief comments */}
          <Card className="p-6">
            <label className="block text-base font-medium text-gray-900 mb-3">
              Some brief comments about today's session:
            </label>
            <textarea
              value={briefComments}
              onChange={(e) => setBriefComments(e.target.value)}
              placeholder="Your answer"
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </Card>
            </>
          )}

          {/* Submit button */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="px-8 py-2 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {submitting ? 'Submitting...' : 'Submit Feedback'}
            </Button>
          </div>
        </form>
        </Card>
      )}

      {/* Session Table */}
      {sessionRows.length === 0 && feedbackList.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
          <div className="flex flex-col items-center justify-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <FiMessageSquare size={32} className="text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No feedback yet</h3>
            <p className="text-gray-500 mb-6 text-sm">
              {isAdmin ? 'No trainers have submitted feedback for this cohort yet.' : 'Start by adding your first session feedback.'}
            </p>
            {!showForm && !isAdmin && (
              <Button
                onClick={() => openFeedbackForm()}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium"
              >
                <FiPlus size={18} />
                Add Your First Feedback
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3.5 px-5 text-sm font-medium text-gray-500">Session</th>
                <th className="text-left py-3.5 px-5 text-sm font-medium text-gray-500">Completed by</th>
                <th className="text-left py-3.5 px-5 text-sm font-medium text-gray-500">Completed on</th>
                <th className="py-3.5 px-5"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {sessionRows.map((row, index) => {
                const hasFeedback = !!row.feedback;
                const isExpanded = expandedRow === index;
                const displayTrainerName = row.feedback?.trainerName || resolvedTrainerName;
                const initials = displayTrainerName
                  ? displayTrainerName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                  : '';

                return (
                  <React.Fragment key={row.name + index}>
                    <tr className={index < sessionRows.length - 1 || isExpanded ? 'border-b border-gray-100' : ''}>
                      <td className="py-4 px-5">
                        <span className="text-sm font-semibold text-gray-900">{row.name}</span>
                      </td>
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-3">
                          {displayTrainerName && (
                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 text-gray-600 text-xs font-semibold flex-shrink-0 overflow-hidden">
                              {initials}
                            </div>
                          )}
                          <span className="text-sm text-gray-700">{displayTrainerName || '—'}</span>
                        </div>
                      </td>
                      <td className="py-4 px-5">
                        <span className="text-sm text-gray-500">
                          {hasFeedback
                            ? formatFeedbackDate(row.feedback!)
                            : row.sessionDate
                              ? new Date(row.sessionDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                              : '—'}
                        </span>
                      </td>
                      <td className="py-4 px-5 text-right">
                        {hasFeedback ? (
                          <button
                            onClick={() => setExpandedRow(isExpanded ? null : index)}
                            className="px-4 py-1.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            {isExpanded ? 'Close' : 'View'}
                          </button>
                        ) : (
                          <button
                            onClick={() => openFeedbackForm(row.name)}
                            className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                          >
                            <FiPlus size={16} />
                            Add Feedback
                          </button>
                        )}
                      </td>
                    </tr>

                    {/* Expanded feedback details */}
                    {isExpanded && row.feedback && (
                      <tr className={index < sessionRows.length - 1 ? 'border-b border-gray-100' : ''}>
                        <td colSpan={4} className="px-5 pb-5 bg-gray-50">
                          <div className="pt-4 space-y-4">
                            {row.feedback.answers && row.feedback.answers.length > 0 ? (
                              /* Dynamic, template-driven answers */
                              <div className="space-y-3">
                                {row.feedback.templateName && (
                                  <p className="text-xs font-semibold text-blue-600 uppercase">
                                    {row.feedback.templateName}
                                  </p>
                                )}
                                {row.feedback.answers.map((a) => (
                                  <div key={a.questionId}>
                                    <p className="text-xs font-medium text-gray-500 uppercase mb-1">
                                      {a.question}
                                    </p>
                                    <p className="text-sm text-gray-900 whitespace-pre-wrap">
                                      {a.answer || '—'}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            ) : (
                            <>
                            {/* Session Info */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Who was trained</p>
                                <p className="text-sm text-gray-900">{row.feedback.whoTrained}</p>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Session Date</p>
                                <p className="text-sm text-gray-900">{row.feedback.date}</p>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Attendance</p>
                                <p className="text-sm text-gray-900">{row.feedback.attendanceCount}</p>
                              </div>
                            </div>

                            {/* Timing */}
                            <div className="border-t pt-4">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Started On Time</p>
                                  <p className="text-sm text-gray-900">
                                    {row.feedback.delegatesOnTime === 'yes' ? 'Yes' : row.feedback.delegatesOnTime === 'no' ? 'No' : 'N/A'}
                                  </p>
                                </div>
                                {row.feedback.lateStartTime && (
                                  <div>
                                    <p className="text-xs font-medium text-gray-500 uppercase mb-1">Late Start</p>
                                    <p className="text-sm text-gray-900">{row.feedback.lateStartTime}</p>
                                  </div>
                                )}
                                <div>
                                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Cameras Off</p>
                                  <p className="text-sm text-gray-900">{row.feedback.camerasOffCount || '0'}</p>
                                </div>
                                {row.feedback.camerasOffReason && (
                                  <div>
                                    <p className="text-xs font-medium text-gray-500 uppercase mb-1">Cameras Off Reason</p>
                                    <p className="text-sm text-gray-900">{row.feedback.camerasOffReason}</p>
                                  </div>
                                )}
                                <div>
                                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Homework Submitted</p>
                                  <p className="text-sm text-gray-900">{row.feedback.homeworkSubmittedCount || '0'}</p>
                                </div>
                              </div>
                            </div>

                            {/* Ratings */}
                            {(row.feedback.sessionFeeling || row.feedback.participationRating) && (
                              <div className="border-t pt-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {row.feedback.sessionFeeling && (
                                    <div>
                                      <p className="text-xs font-medium text-gray-500 uppercase mb-2">Session Rating</p>
                                      <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                                        row.feedback.sessionFeeling === 'Excellent' ? 'bg-green-100 text-green-800' :
                                        row.feedback.sessionFeeling === 'Very good' ? 'bg-blue-100 text-blue-800' :
                                        row.feedback.sessionFeeling === 'Satisfactory' ? 'bg-yellow-100 text-yellow-800' :
                                        row.feedback.sessionFeeling === 'Fair' ? 'bg-orange-100 text-orange-800' :
                                        'bg-red-100 text-red-800'
                                      }`}>
                                        {row.feedback.sessionFeeling}
                                      </span>
                                    </div>
                                  )}
                                  {row.feedback.participationRating && (
                                    <div>
                                      <p className="text-xs font-medium text-gray-500 uppercase mb-2">Participation</p>
                                      <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                                        row.feedback.participationRating === 'Excellent' ? 'bg-green-100 text-green-800' :
                                        row.feedback.participationRating === 'Very good' ? 'bg-blue-100 text-blue-800' :
                                        row.feedback.participationRating === 'Satisfactory' ? 'bg-yellow-100 text-yellow-800' :
                                        row.feedback.participationRating === 'Fair' ? 'bg-orange-100 text-orange-800' :
                                        'bg-red-100 text-red-800'
                                      }`}>
                                        {row.feedback.participationRating}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Comments */}
                            {(row.feedback.briefComments || row.feedback.coverageDetails || row.feedback.specificIssues || row.feedback.notFollowingUp) && (
                              <div className="border-t pt-4 space-y-3">
                                {row.feedback.coverageDetails && (
                                  <div>
                                    <p className="text-xs font-medium text-gray-500 uppercase mb-1">Coverage</p>
                                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{row.feedback.coverageDetails}</p>
                                  </div>
                                )}
                                {row.feedback.briefComments && (
                                  <div>
                                    <p className="text-xs font-medium text-gray-500 uppercase mb-1">Comments</p>
                                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{row.feedback.briefComments}</p>
                                  </div>
                                )}
                                {row.feedback.specificIssues && (
                                  <div>
                                    <p className="text-xs font-medium text-gray-500 uppercase mb-1">Issues</p>
                                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{row.feedback.specificIssues}</p>
                                  </div>
                                )}
                                {row.feedback.notFollowingUp && (
                                  <div>
                                    <p className="text-xs font-medium text-gray-500 uppercase mb-1">Delegates Not Following Up</p>
                                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{row.feedback.notFollowingUp}</p>
                                  </div>
                                )}
                              </div>
                            )}
                            </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TrainerFeedback;